// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@guestsupply/App/Store/Reducers";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import { addToEndOfChain, ApiHandler } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { chain as AddCartLinesChain } from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { chain as AddLinesToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import { chain as AddToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { chain as AddWishListToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import { chain as UpdateCartLine } from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import { chain as ReorderOrderDetailsChain } from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { chain as ReorderOrderHistoryChain } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { chain as ReorderOrderStatusChain } from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import { chain as AddCartLineCollectionFromProductsChain } from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import { chain as AddLinesToCartRequisitionsChain } from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import { chain as PlaceOrderSavedOrderDetailsChain } from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";

type CartLineData = Pick<CartLineModel, "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties">;

type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;

export const XngGaAddProductToCart: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const currentBillTo = getCurrentBillToState(state).value;
    const session = getSession(state);
    const locationId = session?.pickUpWarehouse?.id;
    const currentPage = getCurrentPage(state).type;
    const wishList = getWishListState(state, state.pages.myListDetails.wishListId).value;
    const isMyListDetailsPage = currentPage === "MyListsDetailsPage";
    const isMyOrderListPage = wishList?.name === "My Order Guide";

    if (state.xGoogleAnalytics.includeAddToCartEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        const cart = getCurrentCartState(props.getState()).value;
        let cartLinesToAdd: CartLineData[] = [];
        if (props.apiResult?.cartLines || props.apiParameter?.cartLineCollection?.cartLines) {
            // XNG-Changes: this if condition is for Order History Reorder functionality
            if ((props.apiResult?.cartLines as any)?.cartLines) {
                cartLinesToAdd = (props.apiResult?.cartLines as any)?.cartLines || [];
            } else {
                cartLinesToAdd = props.apiResult?.cartLines || props.apiParameter.cartLineCollection?.cartLines || [];
            }
        } else if (cart?.cartLines?.length) {
            cartLinesToAdd = cart.cartLines;
        } else {
            cartLinesToAdd = [
                {
                    shortDescription: props.apiResult?.shortDescription || "",
                    erpNumber: props.apiResult?.erpNumber || "",
                    pricing: props.apiResult?.pricing || null,
                    qtyOrdered: props.apiResult?.qtyOrdered || null,
                    properties: props.apiResult?.properties,
                },
            ];
        }

        const lastItemAdded = cartLinesToAdd?.[cartLinesToAdd?.length - 1];

        const price = lastItemAdded.pricing?.unitNetPrice || 0;
        const quantity = Number(props.apiResult?.properties?.roundedQtyOrdered) || lastItemAdded.qtyOrdered || 0;
        const value = price * quantity;

        const googleAnalyticsData = {
            event: GOOGLE_EVENTS.ADD_TO_CART,
            ecommerce: {
                currency: state.context.session.currency?.currencyCode,
                value,
                items: [
                    {
                        item_id: lastItemAdded?.erpNumber,
                        item_name: lastItemAdded?.shortDescription,
                        affiliation: currentBillTo?.companyName,
                        index: 0,
                        item_brand: (lastItemAdded as CartLineModel).brand?.name,
                        location_id: locationId,
                        price,
                        quantity,
                        ...parseProductCategories(lastItemAdded?.properties?.categories),
                    },
                ],
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
                element_id: undefined as string | undefined,
            },
        };

        if (isMyListDetailsPage) {
            googleAnalyticsData.ecommerce.element_id = isMyOrderListPage ? "orderguide" : "mylists";
        }

        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        dataLayer.push(googleAnalyticsData);
    }
};

const _GaAddProductToCart = XngGaAddProductToCart as any;
addToEndOfChain(AddToCartChain, _GaAddProductToCart);
addToEndOfChain(UpdateCartLine, _GaAddProductToCart);
addToEndOfChain(AddWishListToCartChain, _GaAddProductToCart);
addToEndOfChain(AddCartLinesChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderDetailsChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderHistoryChain, _GaAddProductToCart);
addToEndOfChain(ReorderOrderStatusChain, _GaAddProductToCart);
addToEndOfChain(AddCartLineCollectionFromProductsChain, _GaAddProductToCart);
addToEndOfChain(AddLinesToCartRequisitionsChain, _GaAddProductToCart);
addToEndOfChain(PlaceOrderSavedOrderDetailsChain, _GaAddProductToCart);
